import React, { useState, useRef, useEffect } from 'react';
import { Search, Check } from 'lucide-react';

interface SearchOption {
	value: string;
	label: string;
	category: string; // 'entityId' or 'error'
}

interface SearchWithSuggestionsProps {
	searchText: string;
	onSearchChange: (text: string, category: string) => void;
	placeholder?: string;
	className?: string;
}

export const SearchWithSuggestions: React.FC<SearchWithSuggestionsProps> = ({
	searchText,
	onSearchChange,
	placeholder = 'Search by Entity ID or Error Message...',
	className = '',
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [inputValue, setInputValue] = useState(searchText);
	const containerRef = useRef<HTMLDivElement>(null);

	// Available search options
	const searchOptions: SearchOption[] = [
		{ value: 'entityId', label: 'Entity ID', category: 'entityId' },
		{ value: 'error', label: 'Error Message', category: 'error' },
	];

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Sync with parent searchText
	useEffect(() => {
		setInputValue(searchText);
	}, [searchText]);

	const handleInputChange = (value: string) => {
		setInputValue(value);
		if (value.trim()) {
			setIsOpen(true);
		} else {
			setIsOpen(false);
		}
	};

	const handleSelectOption = (option: SearchOption) => {
		setInputValue(inputValue); // Keep the search text
		onSearchChange(inputValue, option.category);
		setIsOpen(false);
	};

	const handleInputFocus = () => {
		if (inputValue.trim()) {
			setIsOpen(true);
		}
	};

	return (
		<div ref={containerRef} className={`relative ${className}`}>
			{/* Search Input */}
			<div className="relative">
				<div className="absolute left-3 top-1/2 transform -translate-y-1/2">
					<Search className="h-4 w-4 text-gray-400" />
				</div>
				<input
					type="text"
					value={inputValue}
					onChange={(e) => handleInputChange(e.target.value)}
					onFocus={handleInputFocus}
					placeholder={placeholder}
					className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-400 bg-white transition-colors"
				/>
			</div>

			{/* Suggestions Dropdown */}
			{isOpen && inputValue.trim() && (
				<div
					className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden"
					style={{
						backgroundColor: '#ffffff',
						opacity: 1,
					}}>
					<div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
						<p className="text-xs font-medium text-gray-600">Search in:</p>
					</div>
					{searchOptions.map((option) => (
						<div
							key={option.value}
							onClick={() => handleSelectOption(option)}
							className="px-3 py-2.5 cursor-pointer text-sm transition-colors hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
							style={{
								backgroundColor: '#ffffff',
							}}>
							<div className="flex items-start justify-between">
								<div className="flex-1">
									<div className="flex items-center gap-2 mb-1">
										<span className="text-xs font-medium text-gray-500">{option.label}:</span>
									</div>
									<div className="text-gray-900 font-medium">"{inputValue}"</div>
								</div>
								<Check className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
